/**
 * The staffing module's read seam onto customer-management-api.
 *
 * Every type here mirrors a response shape in
 * `apps/customer-management-api/src/app/api/staffing/*` and nothing more. They
 * are hand-written rather than shared because cm-api installs standalone with
 * npm and is not a pnpm workspace member — the same constraint that keeps the
 * ENGINE out of cm-api (plan D8).
 *
 * `staffing-dto-shape.spec.ts` pins `StoredTierDto` against cm-api's own
 * `StoredTier` — and ONLY that one, which the spec says out loud. This docblock
 * previously claimed the whole file was pinned by a spec that did not exist, so
 * every DTO here was unguarded while reading as guarded. The tier shape is
 * pinned because it is the one both sides WRITE: a config round-trips through
 * `toPublishInput`, so a field cm-api stores and this file does not declare is
 * dropped on the next publish — silently, and permanently, since versions are
 * immutable. Extending the spec to the read-only DTOs is worth doing and is not
 * the same urgency.
 *
 * Reads only. Writes (pin / unpin / config) live in `mutations.ts` so a page
 * that only displays cannot accidentally import a POST.
 */
import { apiFetch } from '@/features/crm/lib/api-fetch';

/** One customer as the staffing book sees it. Mirrors cm-api's StaffingAccount. */
export interface StaffingAccountDto {
  id: string;
  name: string;
  /** Trailing-12-month BOOKING VOLUME in $M — never revenue to Andavo. */
  spendM: number;
  /** Engine key of today's owner, or null. NOT a display name. */
  currentCsm: string | null;
}

export interface AccountsResponse {
  accounts: StaffingAccountDto[];
  /** Engine key → display label. Kept separate so relabelling cannot move a book. */
  csmDisplayNames: Record<string, string>;
  /**
   * customerId → trailing-90-day PNRs, for the incentive plan's thresholds.
   *
   * ALONGSIDE the accounts rather than on them: the engine tiers by spend and
   * has no use for a transaction count, and cm-api holds its account shape in
   * exact parity with the engine's own.
   *
   * A MISSING KEY MEANS NO PNR DATA IS RECORDED, not zero transactions. Reading
   * an absent key as 0 would understate a CSM's book by exactly the customers
   * nobody has data for, and the total would still look like a measurement.
   * Optional here because a version of cm-api without it is still servable —
   * the incentive comparison degrades to "not measured", which is honest.
   */
  pnrs90?: Record<string, number>;
  meta: {
    source: string;
    activeOnly: boolean | null;
    totalRows: number;
    returned: number;
    spendUnit: string;
    readOnly: boolean;
    /** 'none' means the caller is outside the customer-visibility allowlist. */
    scope?: string;
    /** Served customers carrying no PNR data at all. */
    pnrsUnrecorded?: number;
    /** The window `pnrs90` counts. Annualizing is the reader's job, out loud. */
    pnrWindowDays?: number;
    /**
     * Digest of THIS book, to echo back when saving a snapshot.
     *
     * Computed by cm-api and passed through untouched — never recomputed here.
     * The snapshot route compares against its own recomputation, so a second
     * implementation of the hash in the browser would be two things that must
     * agree forever and cannot be tested together.
     *
     * Optional because a cm-api without it is still servable; a client that
     * cannot find it must say saving is unavailable rather than invent one.
     */
    bookFingerprint?: string;
  };
}

/**
 * A tier as STORED. `minSpendM: null` is the catch-all — JSON cannot express
 * -Infinity, so null carries that meaning and `toEngineTiers` is the one place
 * it becomes a number. See cm-api's config.ts for the full reasoning.
 *
 * `loadedCostPerFte` is ABSENT, not null, for a caller without
 * view:staffing_financials. Absent means "you may not see it"; null would mean
 * "no cost is recorded", which is a different fact.
 */
export interface StoredTierDto {
  key: string;
  label: string;
  minSpendM: number | null;
  hoursPerAccountPerMonth: number;
  loadedCostPerFte?: number | null;
  /**
   * How accounts get into this tier. Absent means 'band'.
   *
   * 'band'  — membership is computed from spend. Exactly one band is the
   *           catch-all (minSpendM null) and it must be the last one.
   * 'named' — membership is BY HAND ONLY, via a pin's tierKey. Strategic
   *           accounts are not a size; they come in every size, and a band would
   *           re-tier them every time bookings moved.
   *
   * `minSpendM: null` means OPPOSITE things for the two kinds — "everything
   * below" for a band, "nothing at all" for a named tier — which is exactly why
   * the kind is stated rather than inferred from position.
   */
  selection?: 'band' | 'named';
  /**
   * MONTHLY PNRs one CSM staffing this tier must book to earn the incentive.
   *
   * Monthly because the incentive is calculated and paid monthly — the payout
   * percentage is a share of ANNUAL base earned in twelve instalments, and this
   * bar gates each instalment.
   *
   * Per PERSON, not per tier — the tier sets the bar, everyone staffing it is
   * measured against that same bar. Per-tier because the bar is not the same job
   * everywhere: Enterprise CSMs carry the largest customers, so a higher number
   * is reachable there in a way it is not in Small.
   *
   * NOT REDACTED, unlike `loadedCostPerFte`: it is a booking target, not a cost,
   * and the money half of the incentive (`incentivePct`) is redacted on its own.
   *
   * `null` or absent means NO THRESHOLD IS SET — distinct from a threshold of
   * zero, which everybody clears by existing.
   */
  incentivePnrThreshold?: number | null;
}

export interface StaffingConfigDto {
  version: number;
  tiers: StoredTierDto[];
  hrsPerFteYear: number;
  balanceBy: 'count' | 'spend';
  guardrailBasis: 'profit' | 'revenue';
  note: string | null;
  createdBy: string;
  createdAt: string;
  /** Present only with view:staffing_financials. */
  guardrailPct?: number;
  revenuePct?: number;
  profitPct?: number;
  /**
   * Team-wide incentive as a PERCENTAGE of base salary (0-100, so 10 = 10%).
   * Financial, so absent when redacted. The server defaults it to 0 rather
   * than omitting it for an old version — the client multiplies by it, and
   * `undefined` would make every derived cost NaN.
   */
  incentivePct?: number;
  financialsRedacted: boolean;
}

export interface ConfigResponse {
  config: StaffingConfigDto | null;
  configured: boolean;
}

export interface RosterMemberDto {
  /** DB row id as a string. The engine never sees this — see csmKeyOf(). */
  id: string;
  displayName: string;
  externalId: string | null;
  email: string | null;
  role: string;
  active: boolean;
  /**
   * BASE annual salary. The annual COST is derived — base plus the team-wide
   * `incentivePct` from the config — and is never stored, because a stored
   * total drifts the moment either input moves.
   *
   * ABSENT when the server redacted financials; `null` when it is genuinely
   * unrecorded (a new hire, a vacancy). Those are different states and the API
   * keeps them different, so `?? 0` here would collapse both into "free".
   */
  baseSalaryAnnual?: number | null;
}

export interface RosterResponse {
  roster: RosterMemberDto[];
  meta: {
    includeInactive: boolean;
    returned: number;
    /** True when loadedCostAnnual was withheld from every row. */
    financialsRedacted?: boolean;
  };
}

export interface SlotDto {
  tierKey: string;
  position: number;
  csmId: string | null;
  csmDisplayName: string | null;
  csmActive: boolean | null;
  /** No csmId means a PLANNED HIRE, not missing data. */
  vacancy: boolean;
}

export interface SlotsResponse {
  configVersion: number | null;
  configured: boolean;
  slots: SlotDto[];
}

/**
 * A DECISION about one customer, overriding what the model would compute.
 *
 * Two independent halves, either or both. `csmId` says "stays with this person";
 * `tierKey` says "belongs in this tier". A tier-only decision has NO csmId, and
 * that is the shape a hand-picked tier is made of — so every reader has to
 * handle the null rather than assuming a pin names somebody.
 */
export interface PinDto {
  customerId: string;
  csmId: string | null;
  csmDisplayName: string | null;
  /** A pin to someone who has left is exactly the stale rule a review must see. */
  csmActive: boolean | null;
  /**
   * The tier this customer was placed in BY HAND, beating its spend band.
   *
   * Null for an ordinary pin. Non-null is the only way into a tier declared
   * `selection: 'named'`, which takes nothing by spend at all.
   */
  tierKey: string | null;
  reason: string | null;
  createdBy: string;
  createdAt: string;
}

export interface PinsResponse {
  pins: PinDto[];
  meta: { returned: number };
}

/**
 * The whole staffing read set, fetched together.
 *
 * One call site rather than five hooks: every page needs the same five reads to
 * render anything at all (a book without its config cannot be tiered, and
 * portfolios without slots cannot be distributed), so fetching them
 * independently would only produce more half-rendered intermediate states.
 *
 * `includeInactive` is REQUIRED by /accounts on purpose — which subset of the
 * CRM the staffing book covers is unsettled (#1802) and the caller must say. It
 * is threaded through here rather than defaulted for the same reason.
 */
/**
 * A saved scenario, as the LIST returns it.
 *
 * Deliberately without `portfolios`, `pins` and `slots` — cm-api omits them here
 * because they are the bulk of a row and a picker never renders them. Fetch one
 * by id when you actually need the assignment.
 */
export interface SnapshotSummaryDto {
  id: string;
  configVersion: number;
  bookAccountCount: number;
  /**
   * WHICH SUBSET of the CRM this was computed over.
   *
   * Not a detail: the book fingerprint is a digest of a row set, so a snapshot
   * taken with inactive customers included is a record about a different book
   * than one without. Shown wherever a snapshot is listed, because two rows that
   * differ only by this look identical otherwise.
   */
  includeInactive: boolean;
  engineVersion: string;
  label: string | null;
  createdBy: string;
  createdAt: string;
}

/** One saved scenario in full, including what the model said. */
export interface SnapshotDto extends SnapshotSummaryDto {
  /** `{ customerId: csmRowId }` as applied — the staffing_csm row id. */
  pins: Record<string, string>;
  /**
   * `{ customerId: tierKey }` — hand-picked TIER placements.
   *
   * Separate from `pins` because they are separate inputs: one says who holds a
   * customer, the other says which band it is served at, and a restore that
   * reproduced only the first would put the right person on the wrong tier.
   */
  tierPins: Record<string, string>;
  /** `{ tierKey: (csmRowId | null)[] }` in position order. A null is a vacancy. */
  slots: Record<string, (string | null)[]>;
  portfolios: SnapshotPortfolio[];
  bookFingerprint: string;
}

/**
 * A portfolio as STORED — account IDs, not accounts.
 *
 * The engine's own `Portfolio` carries full `Account` objects. Sending those
 * would put a second copy of the customer book, with names and spend, inside a
 * JSON column — which is exactly what `staffing_snapshot`'s own schema comment
 * says it does not do ("the book itself is NOT copied here"). Ids are what a
 * replay compares: the spend that produced the assignment is already pinned by
 * the fingerprint.
 */
export interface SnapshotPortfolio {
  csm: string;
  tier: string | null;
  assigned: string[];
  pinned: string[];
}

export interface SnapshotListResponse {
  snapshots: SnapshotSummaryDto[];
  meta: { returned: number; limit: number };
}

/** Saved scenarios, newest first. */
export async function fetchSnapshots(): Promise<SnapshotSummaryDto[]> {
  const body = await apiFetch<SnapshotListResponse>('/api/staffing/snapshots');
  return body.snapshots;
}

/** One saved scenario in full — the assignment included. */
export async function fetchSnapshot(id: string): Promise<SnapshotDto> {
  const body = await apiFetch<{ snapshot: SnapshotDto }>(
    `/api/staffing/snapshots?id=${encodeURIComponent(id)}`,
  );
  return body.snapshot;
}

export interface StaffingReadSet {
  accounts: AccountsResponse;
  config: ConfigResponse;
  roster: RosterResponse;
  slots: SlotsResponse;
  pins: PinsResponse;
}

/**
 * The people who have been DEACTIVATED, fetched on demand.
 *
 * A separate call rather than a flag on the shared read set, because "inactive"
 * means two unrelated things in this module and merging them would tie them
 * together: `StaffingDataProvider.includeInactive` is about which CUSTOMERS the
 * book covers (the unsettled #1802 subset question, deliberately a visible
 * control), and this is about which PEOPLE are still on the team. Widening that
 * flag to cover both would make "show me former colleagues" silently re-tier the
 * whole book.
 *
 * Returns ONLY the deactivated rows: the caller already has the active ones, and
 * handing back a superset invites a page to render everybody twice.
 */
export async function fetchFormerMembers(): Promise<RosterMemberDto[]> {
  const body = await apiFetch<RosterResponse>(
    '/api/staffing/roster?includeInactive=1',
  );
  return body.roster.filter((m) => !m.active);
}

export async function fetchStaffingReadSet(opts: {
  includeInactive: boolean;
}): Promise<StaffingReadSet> {
  const inactive = opts.includeInactive ? '1' : '0';
  const [accounts, config, roster, slots, pins] = await Promise.all([
    apiFetch<AccountsResponse>(
      `/api/staffing/accounts?includeInactive=${inactive}`,
    ),
    apiFetch<ConfigResponse>('/api/staffing/config'),
    apiFetch<RosterResponse>('/api/staffing/roster'),
    apiFetch<SlotsResponse>('/api/staffing/slots'),
    apiFetch<PinsResponse>('/api/staffing/pins'),
  ]);
  return { accounts, config, roster, slots, pins };
}
